import { useState, useCallback, useMemo } from 'react';

/**
 * useGroupedData - A hook for grouping data by a key (e.g., examId)
 *
 * @param {Object} config - Configuration object
 * @param {Array} config.data - The data array to group
 * @param {Function} config.getGroupKey - Function to get the group key from an item
 * @param {Function} config.getGroupMeta - Function to get group metadata (name, date, etc.)
 * @param {string} config.itemsKey - Key name for the items array in each group (default: 'items')
 */
const useGroupedData = ({
  data = [],
  getGroupKey,
  getGroupMeta,
  itemsKey = 'items',
} = {}) => {
  const [expandedGroups, setExpandedGroups] = useState({});

  // Group the data
  const groupedData = useMemo(() => {
    if (!data || !getGroupKey) return {};

    return data.reduce((acc, item) => {
      const key = getGroupKey(item) || 'unknown';

      if (!acc[key]) {
        const meta = getGroupMeta ? getGroupMeta(item) : {};
        acc[key] = {
          groupId: key,
          ...meta,
          [itemsKey]: [],
        };
      }

      acc[key][itemsKey].push(item);
      return acc;
    }, {});
  }, [data, getGroupKey, getGroupMeta, itemsKey]);

  // Get groups as array (useful for rendering)
  const groupsArray = useMemo(() => {
    return Object.values(groupedData);
  }, [groupedData]);

  // Get total count across all groups
  const totalCount = useMemo(() => {
    return data?.length || 0;
  }, [data]);

  // Get group count
  const groupCount = useMemo(() => {
    return Object.keys(groupedData).length;
  }, [groupedData]);

  // Toggle a single group's expanded state
  const toggleGroup = useCallback((groupId) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  }, []);

  // Set a specific group's expanded state
  const setGroupExpanded = useCallback((groupId, expanded) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: expanded,
    }));
  }, []);

  // Expand all groups
  const expandAll = useCallback(() => {
    const allExpanded = {};
    Object.keys(groupedData).forEach((key) => {
      allExpanded[key] = true;
    });
    setExpandedGroups(allExpanded);
  }, [groupedData]);

  // Collapse all groups
  const collapseAll = useCallback(() => {
    setExpandedGroups({});
  }, []);

  // Check if a group is expanded
  const isGroupExpanded = useCallback((groupId) => {
    return expandedGroups[groupId] || false;
  }, [expandedGroups]);

  // Initialize expanded state (e.g., all collapsed)
  const initializeExpanded = useCallback((defaultExpanded = false) => {
    const initialState = {};
    Object.keys(groupedData).forEach((key) => {
      initialState[key] = defaultExpanded;
    });
    setExpandedGroups(initialState);
  }, [groupedData]);

  return {
    // Data
    groupedData,
    groupsArray,
    totalCount,
    groupCount,

    // Expansion state
    expandedGroups,
    isGroupExpanded,
    toggleGroup,
    setGroupExpanded,
    expandAll,
    collapseAll,
    initializeExpanded,
  };
};

export default useGroupedData;
